import React, { useCallback, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { downloadCSVTemplate } from '../../../services/inventoryService';

/**
 * ImportUploadStep - Step 1: Upload CSV file
 * Handles drag-and-drop and file selection
 */
function ImportUploadStep({ onFileSelected, file, loading, error }) {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const droppedFile = files[0];
      if (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv')) {
        onFileSelected(droppedFile);
      }
    }
  }, [onFileSelected]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelected(selectedFile);
    }
  };

  const handleClearFile = () => {
    onFileSelected(null);
  };

  const handleDownloadTemplate = () => {
    downloadCSVTemplate('physical_count');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Step 1: Upload CSV File
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Upload a CSV file containing your physical count data. We'll help you map columns in the next step.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Drop zone */}
      {!file ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: dragOver ? 'action.hover' : 'background.paper',
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: dragOver ? 'primary.main' : 'divider',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.main'
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('csv-file-input')?.click()}
        >
          {loading ? (
            <>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1">
                Parsing file...
              </Typography>
            </>
          ) : (
            <>
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Drag & drop your CSV file here
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                or click to browse
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Supports: .csv files
              </Typography>
            </>
          )}

          <input
            id="csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Paper>
      ) : (
        /* File selected */
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <FileIcon sx={{ fontSize: 40, color: 'success.main' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight="medium">
              {file.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloseIcon />}
            onClick={handleClearFile}
          >
            Clear
          </Button>
        </Paper>
      )}

      {/* Expected format info */}
      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Expected Columns
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="error.main">
                Required:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                sku, counted_quantity, warehouse_code, count_date
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="textSecondary">
                Optional:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                batch_number, gtin, asin, expiry_date, unit_cost
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> Column names are flexible - we'll auto-detect common variations like
              "SKU", "Sku", "product_sku", "Qty", "quantity", etc.
            </Typography>
          </Alert>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download Sample Template
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ImportUploadStep;
